/**
 * GameStateHelper - Helper pour gérer les GameStates
 * Identique à Flutter GameStateHelper
 */

import {GameState} from '../models/GameState';
import {GameDataExtraField} from '../models/GameDataExtraField';
import {GameDataAttribute} from '../models/GameDataAttribute';
import GameRegistry from '../systems/GameRegistry';
import {ExtraFieldType} from '../systems/types/ExtraFieldType';

class GameStateHelper {
  /**
   * Initialise les extraFields d'aventure
   * 
   * PRIORITÉ 1 : Si adventureExtraFields est fourni, utilise les valeurs de l'aventure
   * PRIORITÉ 2 : Sinon, utilise les defaultValue des types enregistrés dans GameRegistry (fallback)
   * 
   * @param adventureExtraFields Les extraFields définis dans l'aventure (optionnel)
   * @param adventureType Le type d'aventure pour le fallback (optionnel, requis si pas d'extraFields)
   * @returns Les extraFields initialisés
   */
  static initAdventureExtraFields(
    adventureExtraFields?: any[],
    adventureType?: string,
  ): GameDataExtraField[] {
    // PRIORITÉ 1 : Utiliser les extraFields de l'aventure s'ils existent
    if (adventureExtraFields && adventureExtraFields.length > 0) {
      const fields: GameDataExtraField[] = [];

      for (const adventureField of adventureExtraFields) {
        // Les extraFields de l'aventure peuvent être des objets avec value et type, ou juste des IDs
        if (typeof adventureField === 'number') {
          console.log(`[GameStateHelper] Skipping numeric extraField ID: ${adventureField}`);
          continue;
        }

        const typeName = adventureField.type || adventureField.typeName;
        const value = adventureField.value || '';

        if (!typeName) {
          console.log(`[GameStateHelper] Skipping extraField without type:`, adventureField);
          continue;
        }

        // Récupérer le type d'extraField depuis le GameRegistry
        const extraFieldType = GameRegistry.getExtraFieldType(typeName);
        if (!extraFieldType) {
          console.log(`[GameStateHelper] ExtraField type "${typeName}" not found in registry, skipping`);
          continue;
        }

        // ═══════════════════════════════════════════════════════════════════════════════════════
        // 🚨🚨🚨 CRITIQUE : On évalue UNIQUEMENT à l'init, comme pour les stepExtraFields ! 🚨🚨🚨
        // ═══════════════════════════════════════════════════════════════════════════════════════
        // value contient l'expression (ex: "1D+6")
        // On l'évalue UNE SEULE FOIS et on stocke le résultat dans initValue ET currentValue
        // ═══════════════════════════════════════════════════════════════════════════════════════
        const expression = value || extraFieldType.defaultValue || '';
        
        // Évaluer l'expression UNE SEULE FOIS à l'init
        const evaluatedValue = extraFieldType.generateValue && expression
          ? extraFieldType.generateValue(expression)
          : expression;

        fields.push({
          type: typeName,
          initValue: evaluatedValue, // Valeur évaluée, pas l'expression !
          currentValue: evaluatedValue, // Copie de la valeur évaluée
        });

        console.log(`[GameStateHelper] ⚡ INIT: Evaluated ${typeName} from adventure: expression="${expression}" → evaluatedValue="${evaluatedValue}"`);
      }

      if (fields.length > 0) {
        return fields;
      }
    }

    // PRIORITÉ 2 : Fallback sur les defaultValue des types déclarés dans le système
    if (adventureType) {
      console.log(`[GameStateHelper] No adventure extraFields, using defaults for type: ${adventureType}`);
      const fields: GameDataExtraField[] = [];
      
      // Récupérer le système et utiliser getAdventureExtraFieldTypes() (agnostique)
      const gameSystem = GameRegistry.getSystem(adventureType);
      if (gameSystem && gameSystem.getAdventureExtraFieldTypes) {
        const adventureExtraFieldTypes = gameSystem.getAdventureExtraFieldTypes();
        
        for (const extraFieldType of adventureExtraFieldTypes) {
          if (extraFieldType.defaultValue) {
            const defaultValue = extraFieldType.defaultValue;
            // Évaluer l'expression UNE SEULE FOIS à l'init
            const evaluatedValue = extraFieldType.generateValue 
              ? extraFieldType.generateValue(defaultValue) 
              : defaultValue;
            
            fields.push({
              type: extraFieldType.typeName,
              initValue: evaluatedValue, // Valeur évaluée, pas l'expression !
              currentValue: evaluatedValue, // Copie de la valeur évaluée
            });
            
            console.log(`[GameStateHelper] ⚡ INIT: Evaluated ${extraFieldType.typeName} from defaults: expression="${defaultValue}" → evaluatedValue="${evaluatedValue}"`);
          }
        }
      }

      return fields;
    }

    console.log(`[GameStateHelper] No extraFields to initialize: no adventureExtraFields and no adventureType`);
    return [];
  }

  /**
   * Initialise les extraFields d'une step
   * 
   * PRIORITÉ 1 : Si stepExtraFields est fourni, utilise les valeurs de la step
   * PRIORITÉ 2 : Sinon, utilise les defaultValue des types enregistrés dans GameRegistry (fallback)
   * 
   * Utilise getOrCreate pour ne pas écraser les valeurs existantes si on repasse sur la step
   * 
   * @param gameState Le gameState à mettre à jour
   * @param stepNumber Le numéro de la step
   * @param stepExtraFields Les extraFields définis dans la step (optionnel)
   * @param stepType Le type de step pour le fallback (optionnel, requis si pas d'extraFields)
   */
  static initStepExtraFields(
    gameState: GameState,
    stepNumber: number,
    stepExtraFields?: any[],
    stepType?: string,
  ): void {
    // PRIORITÉ 1 : Utiliser les extraFields de la step s'ils existent
    if (stepExtraFields && stepExtraFields.length > 0) {
      for (const stepField of stepExtraFields) {
        // Les extraFields de la step peuvent être des objets avec value et type, ou juste des IDs
        if (typeof stepField === 'number') {
          console.log(`[GameStateHelper] Skipping numeric extraField ID: ${stepField}`);
          continue;
        }

        const typeName = stepField.type || stepField.typeName;
        const value = stepField.value || '';

        if (!typeName) {
          console.log(`[GameStateHelper] Skipping step extraField without type:`, stepField);
          continue;
        }

        // Récupérer le type d'extraField depuis le GameRegistry (agnostique : utilise le typeName tel quel)
        const extraFieldType = GameRegistry.getExtraFieldType(typeName);
        if (!extraFieldType) {
          console.log(`[GameStateHelper] Step extraField type "${typeName}" not found in registry, skipping`);
          continue;
        }

        // GetOrCreate : récupérer le champ existant ou le créer
        let field = this.getStepExtraField(gameState, stepNumber, typeName);
        
        if (!field) {
          // Créer un nouveau field
          if (!gameState.data.stepExtraFields) {
            gameState.data.stepExtraFields = [];
          }
          field = {
            stepNumber,
            type: typeName,
            initValue: '',
            currentValue: '',
          };
          gameState.data.stepExtraFields.push(field);
        }

        // Si le champ n'a pas encore été initialisé (initValue vide), l'initialiser
        if (!field.initValue || field.initValue === '') {
          // ═══════════════════════════════════════════════════════════════════════════════════════
          // 🚨🚨🚨 CRITIQUE : On évalue UNIQUEMENT à l'init, JAMAIS après ! 🚨🚨🚨
          // ═══════════════════════════════════════════════════════════════════════════════════════
          // step.extraFields[].value contient l'expression (ex: "1D+6")
          // On l'évalue UNE SEULE FOIS avec generateValue() et on stocke le résultat
          // ═══════════════════════════════════════════════════════════════════════════════════════
          const expression = value || '';
          
          if (expression) {
            // Évaluer l'expression UNE SEULE FOIS à l'init
            const evaluatedValue = extraFieldType.generateValue && expression
              ? extraFieldType.generateValue(expression)
              : expression;
            
            // Stocker la valeur évaluée dans initValue ET currentValue
            field.initValue = evaluatedValue;
            field.currentValue = evaluatedValue;
            console.log(`[GameStateHelper] ⚡ INIT: Evaluated step ${typeName} (step ${stepNumber}): expression="${expression}" → evaluatedValue="${evaluatedValue}"`);
          } else {
            // Fallback : utiliser defaultValue et l'évaluer
            const defaultValue = extraFieldType.defaultValue || '';
            const evaluatedValue = extraFieldType.generateValue && defaultValue
              ? extraFieldType.generateValue(defaultValue)
              : defaultValue;
            
            field.initValue = evaluatedValue;
            field.currentValue = evaluatedValue;
            console.log(`[GameStateHelper] ⚡ INIT: Evaluated step ${typeName} (step ${stepNumber}) from defaults: "${defaultValue}" → "${evaluatedValue}"`);
          }
        } else {
          // ═══════════════════════════════════════════════════════════════════════════════════════
          // 🚨🚨🚨 CRITIQUE : Le champ existe déjà, NE JAMAIS RÉÉVALUER ! 🚨🚨🚨
          // ═══════════════════════════════════════════════════════════════════════════════════════
          // initValue ne change JAMAIS une fois initialisé (déjà évalué à l'init)
          // currentValue peut être modifié pendant le combat, on le préserve
          // ═══════════════════════════════════════════════════════════════════════════════════════
          if (!field.currentValue || field.currentValue === '') {
            console.log(`[GameStateHelper] Step ${typeName} (step ${stepNumber}) currentValue was empty, initValue=${field.initValue}`);
          } else {
            console.log(`[GameStateHelper] Step ${typeName} (step ${stepNumber}) already initialized, preserving: initValue=${field.initValue}, currentValue=${field.currentValue}`);
          }
        }
      }
      return;
    }

    // PRIORITÉ 2 : Fallback sur les defaultValue des types déclarés dans le StepType
    if (stepType) {
      console.log(`[GameStateHelper] No step extraFields, using defaults for step type: ${stepType}`);
      
      // Récupérer le StepType et utiliser getStepExtraFieldTypes() (agnostique)
      const stepTypeInstance = GameRegistry.getStepType(stepType);
      if (stepTypeInstance && stepTypeInstance.getStepExtraFieldTypes) {
        const stepExtraFieldTypes = stepTypeInstance.getStepExtraFieldTypes();
        
        for (const extraFieldType of stepExtraFieldTypes) {
          if (extraFieldType.defaultValue) {
            // GetOrCreate : récupérer le champ existant ou le créer
            let field = this.getStepExtraField(gameState, stepNumber, extraFieldType.typeName);
            
            if (!field) {
              if (!gameState.data.stepExtraFields) {
                gameState.data.stepExtraFields = [];
              }
              field = {
                stepNumber,
                type: extraFieldType.typeName,
                initValue: '',
                currentValue: '',
              };
              gameState.data.stepExtraFields.push(field);
            }

            // Si le champ n'a pas encore été initialisé, l'initialiser
            if (!field.initValue || field.initValue === '') {
              const defaultValue = extraFieldType.defaultValue || '';
              // Pour le fallback, on évalue defaultValue car c'est une expression
              const evaluatedValue = extraFieldType.generateValue && defaultValue
                ? extraFieldType.generateValue(defaultValue) 
                : defaultValue;
              
              field.initValue = evaluatedValue;
              field.currentValue = evaluatedValue;
              
              console.log(`[GameStateHelper] ⚡ INIT: Evaluated step ${extraFieldType.typeName} (step ${stepNumber}) from defaults: expression="${defaultValue}" → evaluatedValue="${evaluatedValue}"`);
            } else {
              // Si le champ existe déjà mais currentValue a été modifié, on ne touche à rien
              if (!field.currentValue || field.currentValue === '') {
                console.log(`[GameStateHelper] Step ${extraFieldType.typeName} (step ${stepNumber}) currentValue was empty, initValue=${field.initValue}`);
              }
            }
          }
        }
      }
      return;
    }

    console.log(`[GameStateHelper] No step extraFields to initialize: no stepExtraFields and no stepType`);
  }

  /**
   * Récupère un champ extra d'aventure par type
   */
  static getAdventureExtraField(gameState: GameState, typeName: string): GameDataExtraField | null {
    const field = gameState.data.adventureExtraFields.find((f) => f.type === typeName);
    return field || null;
  }

  /**
   * Récupère un attribut par son ID
   */
  static getAttribute(gameState: GameState, attributeId: string): GameDataAttribute | null {
    const attr = gameState.data.objects.find((obj) => obj.attributeId === attributeId);
    return attr || null;
  }

  /**
   * Vérifie si un attribut existe
   */
  static hasAttribute(gameState: GameState, attributeId: string): boolean {
    return gameState.data.objects.some((obj) => obj.attributeId === attributeId);
  }

  /**
   * Met à jour ou ajoute un champ extra d'aventure
   */
  static addOrUpdateAdventureExtraField(
    gameState: GameState,
    typeName: string,
    value: string | number,
  ): void {
    const fieldIndex = gameState.data.adventureExtraFields.findIndex((f) => f.type === typeName);

    if (fieldIndex >= 0) {
      // Mettre à jour seulement currentValue, préserver initValue
      gameState.data.adventureExtraFields[fieldIndex].currentValue = value;
    } else {
      // Ajouter un nouveau champ
      gameState.data.adventureExtraFields.push({
        type: typeName,
        currentValue: value,
      });
    }
  }

  /**
   * Récupère un champ extra de step par type et numéro de step
   */
  static getStepExtraField(
    gameState: GameState,
    stepNumber: number,
    typeName: string,
  ): GameDataExtraField | null {
    const field = gameState.data.stepExtraFields?.find(
      (f) => f.stepNumber === stepNumber && f.type === typeName,
    );
    return field || null;
  }

  /**
   * Récupère ou crée un champ extra de step dans le gameState
   */
  static getOrCreateGameStateStepExtraField(
    gameState: GameState,
    step: any, // PlayerStep
    typeName: string,
  ): GameDataExtraField {
    let field = this.getStepExtraField(gameState, step.numero, typeName);

    if (!field) {
      // Créer un nouveau field
      field = {
        stepNumber: step.numero,
        type: typeName,
        currentValue: '',
        initValue: '',
      };

      // Ajouter au gameState
      if (!gameState.data.stepExtraFields) {
        gameState.data.stepExtraFields = [];
      }
      gameState.data.stepExtraFields.push(field);
    }

    return field;
  }

  /**
   * Met à jour initValue ET currentValue d'un champ extra d'aventure en même temps
   * 
   * @param gameState Le gameState à mettre à jour
   * @param typeName Le type du champ (ex: "D6_FORCE")
   * @param initValue La nouvelle valeur initValue (optionnel, si non fourni, préserve l'ancienne)
   * @param currentValue La nouvelle valeur currentValue (optionnel, si non fourni, préserve l'ancienne)
   */
  static updateAdventureExtraField(
    gameState: GameState,
    typeName: string,
    initValue?: string | number,
    currentValue?: string | number,
  ): void {
    const fieldIndex = gameState.data.adventureExtraFields?.findIndex(
      (f) => f.type === typeName,
    );

    if (fieldIndex !== undefined && fieldIndex >= 0 && gameState.data.adventureExtraFields) {
      const field = gameState.data.adventureExtraFields[fieldIndex];
      if (initValue !== undefined) {
        field.initValue = initValue;
      }
      if (currentValue !== undefined) {
        field.currentValue = currentValue;
      }
      console.log(`[GameStateHelper] Updated adventure field ${typeName}: initValue=${field.initValue}, currentValue=${field.currentValue}`);
    } else {
      console.warn(`[GameStateHelper] Adventure field not found for update: type=${typeName}`);
    }
  }

  /**
   * Initialise initValue ET currentValue d'un champ extra de step en même temps
   * 
   * @param gameState Le gameState à mettre à jour
   * @param stepNumber Le numéro de l'étape
   * @param typeName Le type du champ (ex: "D6_FORCE_MONSTER")
   * @param initValue La valeur initValue à définir
   * @param currentValue La valeur currentValue à définir (optionnel, si non fourni, utilise initValue)
   */
  static initStepExtraField(
    gameState: GameState,
    stepNumber: number,
    typeName: string,
    initValue: string | number,
    currentValue?: string | number,
  ): void {
    if (!gameState.data.stepExtraFields) {
      gameState.data.stepExtraFields = [];
    }

    const fieldIndex = gameState.data.stepExtraFields.findIndex(
      (f) => f.stepNumber === stepNumber && f.type === typeName,
    );

    const finalCurrentValue = currentValue !== undefined ? currentValue : initValue;

    if (fieldIndex >= 0) {
      // Mettre à jour le champ existant
      gameState.data.stepExtraFields[fieldIndex].initValue = initValue;
      gameState.data.stepExtraFields[fieldIndex].currentValue = finalCurrentValue;
      console.log(`[GameStateHelper] Updated step field ${typeName} (step ${stepNumber}): initValue=${initValue}, currentValue=${finalCurrentValue}`);
    } else {
      // Créer un nouveau champ
      gameState.data.stepExtraFields.push({
        stepNumber,
        type: typeName,
        initValue,
        currentValue: finalCurrentValue,
      });
      console.log(`[GameStateHelper] Created step field ${typeName} (step ${stepNumber}): initValue=${initValue}, currentValue=${finalCurrentValue}`);
    }
  }

  /**
   * Met à jour currentValue d'un champ extra d'aventure (méthode de compatibilité)
   * @deprecated Utiliser updateAdventureExtraField() à la place
   */
  static updateGameStateAdventureExtraFieldValue(
    gameState: GameState,
    field: GameDataExtraField,
    newValue: string | number,
  ): void {
    this.updateAdventureExtraField(gameState, field.type, undefined, newValue);
  }

  /**
   * Met à jour initValue d'un champ extra d'aventure (méthode de compatibilité)
   * @deprecated Utiliser updateAdventureExtraField() à la place
   */
  static updateGameStateAdventureExtraFieldInitValue(
    gameState: GameState,
    field: GameDataExtraField,
    newInitValue: string | number,
  ): void {
    this.updateAdventureExtraField(gameState, field.type, newInitValue, undefined);
  }

  /**
   * Met à jour currentValue d'un champ extra de step (méthode de compatibilité)
   * @deprecated Utiliser initStepExtraField() ou updateAdventureExtraField() à la place
   */
  static updateGameStateStepExtraFieldValue(
    gameState: GameState,
    field: GameDataExtraField,
    newValue: string | number,
  ): void {
    if (!gameState.data.stepExtraFields) {
      gameState.data.stepExtraFields = [];
    }

    const fieldIndex = gameState.data.stepExtraFields.findIndex(
      (f) => f.stepNumber === field.stepNumber && f.type === field.type,
    );

    if (fieldIndex >= 0) {
      gameState.data.stepExtraFields[fieldIndex].currentValue = newValue;
      console.log(`[GameStateHelper] Updated step field ${field.type} (step ${field.stepNumber}): ${newValue}`);
    } else {
      console.warn(`[GameStateHelper] Field not found for update: stepNumber=${field.stepNumber}, type=${field.type}`);
    }
  }

  /**
   * Met à jour initValue d'un champ extra de step (méthode de compatibilité)
   * @deprecated Utiliser initStepExtraField() à la place
   */
  static updateGameStateStepExtraFieldInitValue(
    gameState: GameState,
    field: GameDataExtraField,
    newInitValue: string | number,
  ): void {
    if (!gameState.data.stepExtraFields) {
      gameState.data.stepExtraFields = [];
    }

    const fieldIndex = gameState.data.stepExtraFields.findIndex(
      (f) => f.stepNumber === field.stepNumber && f.type === field.type,
    );

    if (fieldIndex >= 0) {
      gameState.data.stepExtraFields[fieldIndex].initValue = newInitValue;
    } else {
      console.warn(`[GameStateHelper] Field not found for initValue update: stepNumber=${field.stepNumber}, type=${field.type}`);
    }
  }
}

export default GameStateHelper;
